import { useMemo, useState, useCallback, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Link, useOutletContext } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import {
  quizQuestionSets,
  type QuizQuestion,
  type QuizSetId,
} from "../data/sampleQuiz";
import { useSeo } from "../hooks/useSeo";
import { buildCanonicalUrl } from "../seo/config";
import type { LayoutOutletContext } from "../layout/types";
import {
  createModalGraphic,
  createStatusIcon,
  createWallpaperDataUri,
  type LocaleKey,
} from "../utils/svgPlaceholders";
import type { FormEvent, MouseEvent as ReactMouseEvent } from "react";

interface QuestionContent {
  question: string;
  choices: string[];
  answer: number;
}

type ModalState = "success" | "join" | null;
type TryItNowButtonId = QuizSetId;
const MotionButton = motion.button;

export const TryItNow = () => {
  const { t, i18n } = useTranslation();
  const { localePrefix, locale, tryItNowMenuSignal } =
    useOutletContext<LayoutOutletContext>();
  const pageTitle = t("pages.tryItNow.title");
  const siteTitle = t("site.title");
  const instructions = t("pages.tryItNow.instructions");
  const instructionLines = useMemo(
    () => instructions.split(/\r?\n/),
    [instructions],
  );
  const instructionsDescription = useMemo(
    () => instructions.replace(/\s+/g, " ").trim(),
    [instructions],
  );
  useSeo({
    title: `${pageTitle} | ${siteTitle}`,
    description: instructionsDescription,
    canonical: buildCanonicalUrl("try-it-now/", locale),
    locale,
  });
  const [activeSet, setActiveSet] = useState<QuizSetId | null>(null);
  const [hideInactiveButtons, setHideInactiveButtons] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const [showAnswers, setShowAnswers] = useState(false);
  const [showPostSubmitActions, setShowPostSubmitActions] = useState(false);
  const [challengeModalStep, setChallengeModalStep] = useState<
    null | "download" | "complete"
  >(null);
  const questions: QuizQuestion[] = activeSet ? quizQuestionSets[activeSet] : [];

  const createInitialAnswers = useCallback((items: QuizQuestion[]) => {
    return items.reduce<Record<string, number | null>>(
      (acc, item) => ({ ...acc, [item.id]: null }),
      {},
    );
  }, []);

  const [answers, setAnswers] = useState<Record<string, number | null>>(() => {
    return createInitialAnswers(questions);
  });
  const [submitted, setSubmitted] = useState(false);
  const [correctCount, setCorrectCount] = useState(0);
  const [modal, setModal] = useState<ModalState>(null);
  const summaryRef = useRef<HTMLDivElement | null>(null);
  const modalTimeoutRef = useRef<number | null>(null);
  const menuSignalRef = useRef(tryItNowMenuSignal);

  const clearScheduledPopup = useCallback(() => {
    if (modalTimeoutRef.current !== null) {
      if (typeof window !== "undefined") {
        window.clearTimeout(modalTimeoutRef.current);
      }
      modalTimeoutRef.current = null;
    }
  }, []);

  const scheduleChallengePopup = useCallback(
    (step: "download" | "complete") => {
      if (typeof window === "undefined") {
        setChallengeModalStep(step);
        return;
      }
      clearScheduledPopup();
      setChallengeModalStep(null);
      modalTimeoutRef.current = window.setTimeout(() => {
        setChallengeModalStep(step);
        modalTimeoutRef.current = null;
      }, 5000);
    },
    [clearScheduledPopup, setChallengeModalStep],
  );

  const resetQuizState = useCallback(
    (setId: QuizSetId) => {
      clearScheduledPopup();
      const targetQuestions = quizQuestionSets[setId];
      setAnswers(createInitialAnswers(targetQuestions));
      setSubmitted(false);
      setShowAnswers(false);
      setCorrectCount(0);
      setModal(null);
      setChallengeModalStep(null);
      setResetKey((value) => value + 1);
      setShowPostSubmitActions(false);
    },
    [clearScheduledPopup, createInitialAnswers],
  );

  const resetToInitialView = useCallback(() => {
    clearScheduledPopup();
    setActiveSet(null);
    setHideInactiveButtons(false);
    setAnswers(createInitialAnswers([]));
    setSubmitted(false);
    setShowAnswers(false);
    setCorrectCount(0);
    setModal(null);
    setChallengeModalStep(null);
    setResetKey((value) => value + 1);
    setShowPostSubmitActions(false);
  }, [clearScheduledPopup, createInitialAnswers]);

  useEffect(() => {
    if (!activeSet) {
      return;
    }
    resetQuizState(activeSet);
  }, [activeSet, resetQuizState]);

  // Auto-select a set when arriving via URL ?set= parameter (used by Try Again reload)
  useEffect(() => {
    if (activeSet) return;
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const setParam = params.get("set");
    if (setParam === "warmup" || setParam === "challenge") {
      clearScheduledPopup();
      setActiveSet(setParam as TryItNowButtonId);
      setHideInactiveButtons(true);
      setChallengeModalStep(null);
    }
  }, [activeSet, clearScheduledPopup]);

  useEffect(() => {
    if (menuSignalRef.current === tryItNowMenuSignal) {
      return;
    }
    menuSignalRef.current = tryItNowMenuSignal;
    resetToInitialView();
  }, [resetToInitialView, tryItNowMenuSignal]);

  useEffect(
    () => () => {
      clearScheduledPopup();
    },
    [clearScheduledPopup],
  );

  const questionContent = useMemo(
    () =>
      activeSet
        ? questions.map((item) => {
            const content = t(
              `pages.tryItNow.quizSets.${activeSet}.questions.${item.id}`,
              {
                returnObjects: true,
              },
            ) as QuestionContent;
            return { ...content, id: item.id };
          })
        : [],
    [activeSet, questions, t, i18n.language],
  );

  const totalQuestions = questionContent.length;

  const wallpaperHref = useMemo(
    () => createWallpaperDataUri(locale as LocaleKey),
    [locale],
  );

  const joinPath = useMemo(() => {
    const base = localePrefix ? `/${localePrefix}` : "";
    return `${base}/join-now/`;
  }, [localePrefix]);

  const baseUrl = import.meta.env.BASE_URL;

  const buttonGraphics = useMemo(() => {
    const assetBase = `${baseUrl}assets/try-it-now/`;
    const filenames: Record<TryItNowButtonId, string> =
      locale === "en"
        ? {
            warmup: "warmup-h-en.png",
            challenge: "challenge-h-en.png",
          }
        : {
            warmup: "warmup-h.png",
            challenge: "challenge-h.png",
          };

    return (["warmup", "challenge"] as TryItNowButtonId[]).map((id) => {
      const image = `${assetBase}${filenames[id]}`;
      const label = t(`pages.tryItNow.buttons.${id}.label`, {
        defaultValue:
          id === "warmup" ? "Warm-up Questions" : "Challenge Questions",
      });

      return {
        id,
        label,
        image,
      };
    });
  }, [baseUrl, i18n.language, locale, t]);

  const actionButtonImages = useMemo(() => {
    const isEnglish = locale === "en";
    return {
      submit: `${baseUrl}assets/try-it-now/${isEnglish ? "submit-en.png" : "submit.png"}`,
      tryAgain: `${baseUrl}assets/try-it-now/${isEnglish ? "tryagain-en.png" : "tryagain.png"}`,
      tryChallenge: `${baseUrl}assets/try-it-now/${isEnglish ? "challenge-btn-en.png" : "challenge-btn.png"}`,
      tryWarmup: `${baseUrl}assets/try-it-now/${isEnglish ? "warmup-btn-en.png" : "warmup-btn.png"}`,
    };
  }, [baseUrl, locale]);
  const challengeDownloadButtonImage = `${baseUrl}assets/try-it-now/${locale === "en" ? "download-en.png" : "download.png"}`;
  const challengePrizeBackground = `${baseUrl}assets/try-it-now/prize-bg.png`;
  const challengeAwardImage = `${baseUrl}assets/try-it-now/award.png`;
  const challengeCompleteImage = `${baseUrl}assets/try-it-now/loudspeaker.png`;
  const challengeDownloadFileHref = `${baseUrl}downloads/CPCE%20Wallpaper.jpg`;

  // Challenge popup title/message images by step and locale
  const challengeTitleImage = useMemo(() => {
    const en = locale === "en";
    if (challengeModalStep === "download") {
      return `${baseUrl}assets/popup/${en ? "congratulation-title-en.png" : "congratulation-title.png"}`;
    }
    // complete
    return `${baseUrl}assets/popup/${en ? "ready-title-en.png" : "ready-title.png"}`;
  }, [baseUrl, challengeModalStep, locale]);

  const challengeMessageImage = useMemo(() => {
    const en = locale === "en";
    if (challengeModalStep === "download") {
      return `${baseUrl}assets/popup/${en ? "congratulation-message-en.png" : "congratulation-message.png"}`;
    }
    // complete
    return `${baseUrl}assets/popup/${en ? "ready-message-en.png" : "ready-message.png"}`;
  }, [baseUrl, challengeModalStep, locale]);

  // Preload "complete" step images once the download step appears (helps iOS)
  useEffect(() => {
    if (challengeModalStep !== "download") return;
    const en = locale === "en";
    const toPreload = [
      `${baseUrl}assets/popup/${en ? "ready-title-en.png" : "ready-title.png"}`,
      `${baseUrl}assets/popup/${en ? "ready-message-en.png" : "ready-message.png"}`,
    ];
    toPreload.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, [baseUrl, challengeModalStep, locale]);

  const handleButtonSelect = useCallback(
    (id: TryItNowButtonId) => {
      clearScheduledPopup();
      if (id === activeSet) {
        if (hideInactiveButtons) {
          setHideInactiveButtons(false);
        }
        return;
      }
      setActiveSet(id);
      setHideInactiveButtons(true);
      setChallengeModalStep(null);
      if (typeof window !== "undefined") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    },
    [activeSet, clearScheduledPopup, hideInactiveButtons],
  );

  const handleSelect = (questionId: string, choiceIndex: number) => {
    setAnswers((prev) => ({ ...prev, [questionId]: choiceIndex }));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!activeSet) {
      return;
    }
    setSubmitted(true);
    setShowAnswers(true);
    setShowPostSubmitActions(true);

    const score = questionContent.reduce((acc, question) => {
      const selected = answers[question.id];
      if (selected != null && selected === question.answer) {
        return acc + 1;
      }
      return acc;
    }, 0);
    setCorrectCount(score);

    if (activeSet === "challenge") {
      if (score === totalQuestions && totalQuestions > 0) {
        scheduleChallengePopup("download");
      } else if (totalQuestions > 0) {
        scheduleChallengePopup("complete");
      } else {
        setChallengeModalStep(null);
      }
    } else {
      clearScheduledPopup();
      setChallengeModalStep(null);
    }

    setModal(null);

    if (typeof window !== "undefined") {
      window.requestAnimationFrame(() => {
        summaryRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    }
  };

  const handleTryAgain = () => {
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      if (activeSet) {
        url.searchParams.set("set", activeSet);
      }
      window.location.replace(url.toString());
      return;
    }
    if (activeSet) {
      resetQuizState(activeSet);
    } else {
      resetToInitialView();
    }
  };

  const handleTryChallenge = () => {
    setShowPostSubmitActions(false);
    clearScheduledPopup();
    if (activeSet === "challenge") {
      resetQuizState("challenge");
      if (typeof window !== "undefined") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
      return;
    }
    handleButtonSelect("challenge");
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleTryWarmup = () => {
    setShowPostSubmitActions(false);
    clearScheduledPopup();
    if (activeSet === "warmup") {
      resetQuizState("warmup");
      if (typeof window !== "undefined") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
      return;
    }
    handleButtonSelect("warmup");
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleChallengeDownload = (
    e: ReactMouseEvent<HTMLAnchorElement>,
  ) => {
    clearScheduledPopup();
    // Prevent current tab navigation; open in new tab (iOS-friendly)
    e.preventDefault();
    if (typeof window !== "undefined") {
      const href = challengeDownloadFileHref;
      const a = document.createElement("a");
      a.href = href;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => setChallengeModalStep("complete"), 50);
    } else {
      setChallengeModalStep("complete");
    }
  };

  const handleChallengeClose = () => {
    clearScheduledPopup();
    setChallengeModalStep(null);
  };

  const renderStatusIcon = (questionId: string, choiceIndex: number) => {
    if (!submitted) return null;
    const selected = answers[questionId];
    if (selected !== choiceIndex) return null;
    const question = questionContent.find((item) => item.id === questionId);
    if (!question) return null;
    const type =
      selected === question.answer ? ("correct" as const) : ("incorrect" as const);
    return (
      <img
        src={createStatusIcon(type)}
        alt={t(
          `pages.tryItNow.icon.${type === "correct" ? "correct" : "incorrect"}`,
        )}
        className={`quiz-choice__status quiz-choice__status--${type}`}
      />
    );
  };

  const isChallengeDownloadStep = challengeModalStep === "download";
  const challengeIllustrationSrc = isChallengeDownloadStep
    ? challengeAwardImage
    : challengeCompleteImage;
  const challengeIllustrationAlt = isChallengeDownloadStep
    ? t("pages.tryItNow.challengePerfect.awardAlt")
    : t("pages.tryItNow.challengePerfect.completeAlt", {
        defaultValue: t("pages.tryItNow.challengePerfect.awardAlt"),
      });

  return (
    <article className="page page--try-it-now">
      {!activeSet ? (
        <p className="page__intro page__intro--join">
          {instructionLines.map((line, index) => (
            <span key={`try-it-now-instruction-${index}`}>
              {line}
              {index < instructionLines.length - 1 ? <br /> : null}
            </span>
          ))}
        </p>
      ) : null}

      <div className="try-it-now__graphics">
      {buttonGraphics
          .filter(({ id }) => !hideInactiveButtons || id === activeSet)
          .map(({ id, image, label }) => {
            const isActive = id === activeSet;
            return (
              <MotionButton
                key={id}
                type="button"
                className={`try-it-now__button${
                  isActive ? " try-it-now__button--active" : ""
                }`}
                aria-label={label}
                aria-pressed={isActive}
                onClick={() => handleButtonSelect(id)}
                whileHover={{ scale: [null, 1.2, 1.1] }}
                whileTap={{ scale: 1.05 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
              >
                <img
                  src={image}
                  alt={label}
                  className="try-it-now__button-image"
                />
              </MotionButton>
            );
          })}
      </div>

      {activeSet === "challenge" ? (
        <p className="try-it-now__challenge-note">
          {locale === "en"
            ? "Win a mysterious gift if succeeds!"
            : "成功挑戰可獲得神秘禮品！"}
        </p>
      ) : null}

      {activeSet ? (
        <div className="page__sections">
          <section className="page-section">
            <form className="quiz-form" onSubmit={handleSubmit}>
              {submitted ? (
                <div
                  ref={summaryRef}
                  className="quiz-summary"
                  aria-live="polite"
                >
                  <p className="quiz-summary__score">
                    <Trans
                      i18nKey="pages.tryItNow.score"
                      values={{ correct: correctCount, total: totalQuestions }}
                      components={{
                        highlight: (
                          <span className="quiz-summary__score-highlight" />
                        ),
                      }}
                    />
                  </p>
                </div>
              ) : null}
              <ol key={resetKey} className="quiz-list">
                {questionContent.map((question, index) => (
                  <li key={`${question.id}-${resetKey}`} className="quiz-item">
                    <h2 className="quiz-item__title">
                      <img
                        src={`${baseUrl}assets/try-it-now/Q${index + 1}${
                          activeSet === "challenge" ? "-blue" : ""
                        }.png`}
                        alt={t("pages.tryItNow.questionNumberAlt", {
                          number: index + 1,
                        })}
                        className="quiz-item__number"
                      />
                      <span className="quiz-item__question">{question.question}</span>
                    </h2>
                    <div role="radiogroup" className="quiz-choice-group">
                      {question.choices.map((choice, choiceIndex) => {
                        const isSelected = answers[question.id] === choiceIndex;
                        const choiceLetter = String.fromCharCode(65 + choiceIndex);
                        const isCorrectAnswer = choiceIndex === question.answer;
                        const showFeedback = showAnswers;
                        const isCorrectChoice = showFeedback && isCorrectAnswer;
                        const isSelectedCorrect =
                          showFeedback && isSelected && isCorrectAnswer;
                        const isIncorrectSelection =
                          showFeedback && isSelected && !isCorrectAnswer;
                        const classNames = ["quiz-choice"];
                        if (isSelected) {
                          classNames.push("quiz-choice--selected");
                        }
                        if (isCorrectChoice) {
                          classNames.push("quiz-choice--correct");
                        }
                        if (isSelectedCorrect) {
                          classNames.push("quiz-choice--selected-correct");
                        }
                        if (isIncorrectSelection) {
                          classNames.push("quiz-choice--incorrect");
                        }
                        return (
                          <label
                            key={`${question.id}-${choiceIndex}-${resetKey}`}
                            className={classNames.join(" ")}
                            data-letter={choiceLetter}
                          >
                            <input
                              type="radio"
                              name={question.id}
                              value={choiceIndex}
                              checked={isSelected}
                              onChange={() => handleSelect(question.id, choiceIndex)}
                            />
                            <span className="quiz-choice__letter" aria-hidden="true">
                              {choiceLetter}
                            </span>
                            <span className="quiz-choice__text">{choice}</span>
                            {renderStatusIcon(question.id, choiceIndex)}
                          </label>
                        );
                      })}
                    </div>
                  </li>
                ))}
              </ol>
              <div className="quiz-actions">
                {showPostSubmitActions ? (
                  activeSet === "warmup" ? (
                    <>
                      <MotionButton
                        type="button"
                        className="quiz-actions__button"
                        aria-label={t("pages.tryItNow.reset")}
                        onClick={handleTryAgain}
                        whileHover={{ scale: [null, 1.2, 1.1] }}
                        whileTap={{ scale: 1.05 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                      >
                        <img
                          src={actionButtonImages.tryAgain}
                          alt={t("pages.tryItNow.reset")}
                          className="quiz-actions__button-image"
                        />
                      </MotionButton>
                      <MotionButton
                        type="button"
                        className="quiz-actions__button"
                        aria-label={t("pages.tryItNow.buttons.challenge.label", {
                          defaultValue: "Challenge Questions",
                        })}
                        onClick={handleTryChallenge}
                        whileHover={{ scale: [null, 1.2, 1.1] }}
                        whileTap={{ scale: 1.05 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                      >
                        <img
                          src={actionButtonImages.tryChallenge}
                          alt={t("pages.tryItNow.buttons.challenge.label", {
                            defaultValue: "Challenge Questions",
                          })}
                          className="quiz-actions__button-image"
                        />
                      </MotionButton>
                    </>
                  ) : (
                    <>
                      <MotionButton
                        type="button"
                        className="quiz-actions__button"
                        aria-label={t("pages.tryItNow.reset")}
                        onClick={handleTryAgain}
                        whileHover={{ scale: [null, 1.2, 1.1] }}
                        whileTap={{ scale: 1.05 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                      >
                        <img
                          src={actionButtonImages.tryAgain}
                          alt={t("pages.tryItNow.reset")}
                          className="quiz-actions__button-image"
                        />
                      </MotionButton>
                      <MotionButton
                        type="button"
                        className="quiz-actions__button"
                        aria-label={t("pages.tryItNow.buttons.warmupAction.label", {
                          defaultValue: "Try Warm-up Questions",
                        })}
                        onClick={handleTryWarmup}
                        whileHover={{ scale: [null, 1.2, 1.1] }}
                        whileTap={{ scale: 1.05 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                      >
                        <img
                          src={actionButtonImages.tryWarmup}
                          alt={t("pages.tryItNow.buttons.warmupAction.label", {
                            defaultValue: "Try Warm-up Questions",
                          })}
                          className="quiz-actions__button-image"
                        />
                      </MotionButton>
                    </>
                  )
                ) : (
                  <MotionButton
                    type="submit"
                    className="quiz-actions__button"
                    aria-label={t("pages.tryItNow.submit")}
                    whileHover={{ scale: [null, 1.2, 1.1] }}
                    whileTap={{ scale: 1.05 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                  >
                    <img
                      src={actionButtonImages.submit}
                      alt={t("pages.tryItNow.submit")}
                      className="quiz-actions__button-image"
                    />
                  </MotionButton>
                )}
              </div>
            </form>
          </section>
        </div>
      ) : null}

      {modal && (
        <div className="modal-overlay" role="presentation">
          <div
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby={`modal-${modal}-title`}
          >
            <img
              src={createModalGraphic(modal, locale as LocaleKey)}
              alt=""
              className="modal__graphic"
              aria-hidden="true"
            />
            <div className="modal__content">
              <h2 id={`modal-${modal}-title`} className="modal__title">
                {t(`pages.tryItNow.${modal}.title`)}
              </h2>
              <p className="modal__message">
                {t(`pages.tryItNow.${modal}.message`)}
              </p>
              {modal === "success" ? (
                <div className="modal__actions">
                  <a
                    href={wallpaperHref}
                    download={`cpce-quiz-2025-${locale}.svg`}
                    className="button-link"
                    onClick={() => setModal("join")}
                  >
                    {t("pages.tryItNow.success.download")}
                  </a>
                  <button
                    type="button"
                    className="button-link button-link--secondary"
                    onClick={() => setModal("join")}
                  >
                    {t("pages.tryItNow.success.continue")}
                  </button>
                </div>
              ) : (
                <div className="modal__actions">
                  <Link
                    to={joinPath}
                    className="button-link"
                    onClick={() => setModal(null)}
                  >
                    {t("pages.tryItNow.join.cta")}
                  </Link>
                  <button
                    type="button"
                    className="button-link button-link--secondary"
                    onClick={() => setModal(null)}
                  >
                    {t("pages.tryItNow.reset")}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {challengeModalStep && (
        <div className="challenge-popup__overlay" role="presentation">
          <div
            className="challenge-popup"
            role="dialog"
            aria-modal="true"
            aria-labelledby={`challenge-popup-${challengeModalStep}-title`}
            style={{ backgroundImage: `url(${challengePrizeBackground})` }}
          >
            <button
              type="button"
              className="challenge-popup__close"
              aria-label={t("pages.tryItNow.challengePerfect.close")}
              onClick={handleChallengeClose}
            >
              ×
            </button>
            <img
              src={challengeIllustrationSrc}
              alt={challengeIllustrationAlt}
              className="challenge-popup__award"
            />
            <div className="challenge-popup__body">
              <h2
                id={`challenge-popup-${challengeModalStep}-title`}
                className="challenge-popup__heading"
              >
                <img
                  src={challengeTitleImage}
                  alt={t(
                    `pages.tryItNow.challengePerfect.${challengeModalStep}.heading`,
                  )}
                />
              </h2>
              <p className="challenge-popup__message">
                <img
                  src={challengeMessageImage}
                  alt={t(
                    `pages.tryItNow.challengePerfect.${challengeModalStep}.message`,
                  )}
                />
              </p>
              <div className="challenge-popup__actions">
                {challengeModalStep === "download" ? (
                  <>
                    <a
                      href={challengeDownloadFileHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="challenge-popup__download"
                      onClick={handleChallengeDownload}
                    >
                      <img
                        src={challengeDownloadButtonImage}
                        alt={t(
                        "pages.tryItNow.challengePerfect.download.buttonAlt",
                      )}
                    />
                  </a>
                  </>
                ) : (
                  <Link
                    to={joinPath}
                    className="challenge-popup__button"
                    onClick={() => {
                      handleChallengeClose();
                      if (typeof window !== "undefined") {
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }
                    }}
                  >
                    {t("pages.tryItNow.challengePerfect.complete.cta", {
                      defaultValue: t("pages.tryItNow.join.cta"),
                    })}
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </article>
  );
};
